import { Controller } from "@hotwired/stimulus"

// Input mask controller.
//
// Usage (HTML):
//   <input data-controller="mask"
//          data-mask-type-value="phone-br"
//          data-action="input->mask#format">
//
// Or via the UI helper (which wires both attributes automatically):
//   <%= ui :input, form: f, field: :phone, mask: :phone %>
//
// Supported types:
//   - phone       → auto: BR mask for digits; passthrough (only digits + leading "+") for "+..." (international).
//                   BR rules: short codes 3-4 digits (190, 0800) stay raw; 8-digit local "9999-9999";
//                   10/11-digit with DDD "(99) 9999-9999" / "(99) 99999-9999".
//   - phone-br    → forces BR mask only (ignores "+").
//   - phone-intl  → strips everything but "+" and digits; no formatting.
//   - cpf         → "999.999.999-99" (11 digits)
//   - cnpj        → "99.999.999/9999-99" (14 digits)
//   - cep         → "99999-999" (8 digits)
//   - cpf-cnpj    → auto: CPF up to 11 digits, switches to CNPJ at 12+
//   - digits      → strips non-digits, no separators (good for numeric-only IDs)
//   - money       → currency-aware. Reads `data-mask-currency-value` (default: "BRL").
//                   Type as digits; mask formats automatically based on currency rules.
//                   BR/EUR/ARS:  "1.234,56" | US/GBP/JPY: "1,234.56" | JPY/CLP: no decimals.
//                   On form submit: value is stripped to digit-only cents (e.g. "123456")
//                   so the backend receives an integer ready for *_cents columns.
//   - date        → locale-aware date mask. pt-BR/es: "18/03/2026" | en: "03/18/2026".
//                   Pairs with date_picker_controller — both can coexist on the same input.
//   - datetime    → date + time: "18/03/2026 14:30" (or "03/18/2026 14:30" in en).
//   - time        → 24h time: "14:30".
//
// All formatters are pure: they take a raw string, return the formatted string.
// Caret restoration is best-effort — we re-place the caret at the end after every
// keystroke, which is acceptable for masked inputs (and matches user expectation).
const CURRENCY_META = {
  BRL: { decimals: 2, thousands: ".", decimal: "," },
  USD: { decimals: 2, thousands: ",", decimal: "." },
  EUR: { decimals: 2, thousands: ".", decimal: "," },
  GBP: { decimals: 2, thousands: ",", decimal: "." },
  ARS: { decimals: 2, thousands: ".", decimal: "," },
  CLP: { decimals: 0, thousands: ".", decimal: "," },
  JPY: { decimals: 0, thousands: ",", decimal: "." }
}

export default class extends Controller {
  static values = {
    type:     { type: String, default: "phone" },
    currency: { type: String, default: "BRL" }
  }

  connect() {
    this.format()
    if (this.typeValue.startsWith("money") && this.element.form) {
      this.boundUnmask = this.unmaskOnSubmit.bind(this)
      this.element.form.addEventListener("submit", this.boundUnmask)
    }
  }

  disconnect() {
    if (this.boundUnmask && this.element.form) {
      this.element.form.removeEventListener("submit", this.boundUnmask)
    }
  }

  format() {
    const formatted = this.apply(this.element.value)
    if (formatted !== this.element.value) {
      this.element.value = formatted
      // Keep caret at end — most predictable behavior for typed input.
      this.element.setSelectionRange(formatted.length, formatted.length)
    }
  }

  // Strip mask before submit so backend receives integer cents (e.g. "1.234,56" → "123456").
  // Preserva o sinal negativo (estorno): "-11,00" → "-1100". Só o money tem sinal;
  // os demais valores mascarados são sempre positivos.
  unmaskOnSubmit() {
    const sign = isNegative(this.element.value) ? "-" : ""
    const digits = onlyDigits(this.element.value)
    this.element.value = digits ? sign + digits : digits
  }

  apply(value) {
    switch (this.typeValue) {
      case "phone":      return formatPhoneAuto(value)
      case "phone-br":   return formatPhoneBR(onlyDigits(value))
      case "phone-intl": return formatPhoneIntl(value)
      case "cpf":        return formatCPF(onlyDigits(value))
      case "cnpj":       return formatCNPJ(onlyDigits(value))
      case "cep":        return formatCEP(onlyDigits(value))
      case "cpf-cnpj":   return formatCpfOrCnpj(onlyDigits(value))
      case "digits":     return onlyDigits(value)
      case "money":      return formatMoneySigned(value, this.currencyValue)
      case "date":       return formatDate(onlyDigits(value), currentDateOrder())
      case "datetime":   return formatDateTime(onlyDigits(value), currentDateOrder())
      case "time":       return formatTime(onlyDigits(value))
      default:           return value
    }
  }
}

// ---------------------------------------------------------------------------
// Pure formatters — exported via the controller so they're easy to unit test
// from the browser console (e.g. `Stimulus.controllers[0].apply("...")`).
// ---------------------------------------------------------------------------

function onlyDigits(s) { return (s || "").replace(/\D/g, "") }

// Sinal negativo só faz sentido no money (estorno). Considera negativo quando há
// um "-" antes do primeiro dígito — ignora hífens que não sejam de sinal.
function isNegative(s) {
  const v = (s || "").trim()
  const firstDigit = v.search(/\d/)
  return firstDigit === -1 ? v.startsWith("-") : v.slice(0, firstDigit).includes("-")
}

// Money com sinal: formata o valor absoluto e re-prefixa o "-" quando negativo.
//   formatMoneySigned("-1100", "BRL") → "-11,00"
//   formatMoneySigned("1100",  "BRL") → "11,00"
function formatMoneySigned(value, currencyCode) {
  const formatted = formatMoney(onlyDigits(value), currencyCode)
  return formatted && isNegative(value) ? "-" + formatted : formatted
}

function formatPhoneIntl(s) {
  // Keep leading "+" plus all digits, drop everything else.
  const v = (s || "").trim()
  const plus = v.startsWith("+") ? "+" : ""
  return plus + onlyDigits(v)
}

function formatPhoneAuto(s) {
  const v = (s || "").trim()
  if (v.startsWith("+")) return formatPhoneIntl(v)
  return formatPhoneBR(onlyDigits(v))
}

function formatPhoneBR(d) {
  // Short emergency / utility codes — preserve as raw digits (no formatting).
  // 3-digit (190, 192, 193, 911, etc) and 4-digit codes pass through.
  if (d.length <= 4) return d

  // 0800 / 0300 service numbers: "0800 999 9999"
  if (d.startsWith("0800") || d.startsWith("0300")) {
    if (d.length <= 7) return `${d.slice(0, 4)} ${d.slice(4)}`
    return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7, 11)}`
  }

  // Local (no DDD): 8 digits  → "9999-9999"
  // Local (no DDD): 9 digits  → "99999-9999"
  if (d.length <= 5)  return d
  if (d.length <= 8)  return `${d.slice(0, 4)}-${d.slice(4)}`
  if (d.length === 9) return `${d.slice(0, 5)}-${d.slice(5)}`

  // With DDD: 10 digits → "(99) 9999-9999" | 11 digits → "(99) 99999-9999"
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`

  // 12+ digits: assume international entered without "+" — keep raw digits.
  return d
}

function formatCPF(d) {
  d = d.slice(0, 11)
  if (d.length <= 3)  return d
  if (d.length <= 6)  return `${d.slice(0, 3)}.${d.slice(3)}`
  if (d.length <= 9)  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
}

function formatCNPJ(d) {
  d = d.slice(0, 14)
  if (d.length <= 2)  return d
  if (d.length <= 5)  return `${d.slice(0, 2)}.${d.slice(2)}`
  if (d.length <= 8)  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
}

function formatCEP(d) {
  d = d.slice(0, 8)
  if (d.length <= 5) return d
  return `${d.slice(0, 5)}-${d.slice(5)}`
}

function formatCpfOrCnpj(d) {
  return d.length <= 11 ? formatCPF(d) : formatCNPJ(d)
}

// Money formatter. Input: raw digit string (cents). Output: locale-formatted string.
//   formatMoney("123456", "BRL")  → "1.234,56"
//   formatMoney("99",     "BRL")  → "0,99"
//   formatMoney("1",      "BRL")  → "0,01"
//   formatMoney("123456", "JPY")  → "123,456"   (no decimals)
//   formatMoney("",       "BRL")  → ""
function formatMoney(d, currencyCode) {
  if (!d) return ""
  const meta = CURRENCY_META[currencyCode] || CURRENCY_META.BRL
  const decimals = meta.decimals

  if (decimals === 0) {
    return groupThousands(d, meta.thousands)
  }

  const padded = d.padStart(decimals + 1, "0")
  const whole  = padded.slice(0, -decimals)
  const frac   = padded.slice(-decimals)
  return groupThousands(whole, meta.thousands) + meta.decimal + frac
}

function groupThousands(digitStr, sep) {
  return digitStr.replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, sep) || "0"
}

// Date/time formatters — pure (input: digit string, output: formatted string).
// Locale order é detectado via <html lang>: "ymd" (en) ou "dmy" (default).
//
//   formatDate("18032026", "dmy")        → "18/03/2026"
//   formatDate("18", "dmy")              → "18"
//   formatDate("1803", "dmy")            → "18/03"
//   formatDate("03182026", "mdy")        → "03/18/2026"
//
//   formatDateTime("180320261430", "dmy") → "18/03/2026 14:30"
//   formatTime("1430")                    → "14:30"

// Lê a ordem da data via <html lang>. EN = MDY, resto = DMY. Hoje usamos só
// pra documentar a intenção — formatters tratam pos 1/2 como o primeiro grupo,
// 3/4 como segundo, independente do significado semântico (dia vs mês).
function currentDateOrder() {
  const lang = (typeof document !== "undefined" && document.documentElement.lang) || "pt-BR"
  return lang.startsWith("en") ? "mdy" : "dmy"
}

function formatDate(d, _order) {
  d = d.slice(0, 8) // DDMMYYYY ou MMDDYYYY
  if (d.length <= 2) return d
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`
}

function formatDateTime(d, order) {
  d = d.slice(0, 12) // DDMMYYYY + HHMM
  const date = formatDate(d.slice(0, 8), order)
  if (d.length <= 8) return date
  if (d.length <= 10) return `${date} ${d.slice(8)}`
  return `${date} ${d.slice(8, 10)}:${d.slice(10)}`
}

function formatTime(d) {
  d = d.slice(0, 4) // HHMM
  if (d.length <= 2) return d
  return `${d.slice(0, 2)}:${d.slice(2)}`
}

// Exporta formatters pra que date_picker_controller possa usá-los no altInput
// (que Flatpickr cria em runtime e Stimulus não tem como auto-conectar).
export const maskFormatters = {
  date:     (d) => formatDate(d, currentDateOrder()),
  datetime: (d) => formatDateTime(d, currentDateOrder()),
  time:     (d) => formatTime(d)
}
